import {
  createSlideFn,
  deleteSlideFn,
  listAllSlidesOptions,
  toggleSlideActiveFn,
} from "@/actions/hero-slide/handler";
import { listMediaOptions } from "@/actions/media/handler";
import { ImageSelector } from "@/components/admin/ImageSelector";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader } from "@/components/ui/card";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { useMutation, useSuspenseQuery } from "@tanstack/react-query";
import {
  Link,
  createFileRoute,
  redirect,
  useRouteContext,
} from "@tanstack/react-router";
import { ArrowLeft, Eye, EyeOff, Loader2, Plus, Trash2 } from "lucide-react";
import { useState } from "react";
import { toast } from "sonner";

export const Route = createFileRoute("/_app/admin/hero-slides")({
  beforeLoad: async ({ context }) => {
    if (!context.user || context.user.role !== "admin") {
      throw redirect({ to: "/" });
    }
  },
  loader: async ({ context }) => {
    await context.queryClient.ensureQueryData(listAllSlidesOptions());
    await context.queryClient.ensureQueryData(listMediaOptions());
  },
  component: RouteComponent,
});

function RouteComponent() {
  const { data: slidesData } = useSuspenseQuery(listAllSlidesOptions());
  const { queryClient } = useRouteContext({ from: "/_app/admin/hero-slides" });

  const slides = slidesData.body?.slides || [];

  const [isAddDialogOpen, setIsAddDialogOpen] = useState(false);
  const [deleteSlideId, setDeleteSlideId] = useState<string | null>(null);

  // Form state
  const [objectKey, setObjectKey] = useState("");
  const [title, setTitle] = useState("");
  const [subtitle, setSubtitle] = useState("");
  const [displayOrder, setDisplayOrder] = useState("0");
  const [ctaText, setCtaText] = useState("");
  const [ctaLink, setCtaLink] = useState("");

  const createMutation = useMutation({
    mutationFn: createSlideFn,
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: listAllSlidesOptions().queryKey,
      });
      toast.success("Slide başarıyla eklendi");
      setIsAddDialogOpen(false);
      // Reset form
      setObjectKey("");
      setTitle("");
      setSubtitle("");
      setDisplayOrder("0");
      setCtaText("");
      setCtaLink("");
    },
    onError: () => {
      toast.error("Slide eklenirken hata oluştu");
    },
  });

  const toggleMutation = useMutation({
    mutationFn: toggleSlideActiveFn,
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: listAllSlidesOptions().queryKey,
      });
      toast.success("Slide durumu güncellendi");
    },
    onError: () => {
      toast.error("Durum güncellenirken hata oluştu");
    },
  });

  const deleteMutation = useMutation({
    mutationFn: deleteSlideFn,
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: listAllSlidesOptions().queryKey,
      });
      toast.success("Slide silindi");
      setDeleteSlideId(null);
    },
    onError: () => {
      toast.error("Slide silinirken hata oluştu");
    },
  });

  const handleCreateSlide = () => {
    if (!objectKey) {
      toast.error("Lütfen bir görsel seçin");
      return;
    }

    createMutation.mutate({
      data: {
        objectKey,
        displayOrder: Number.parseInt(displayOrder) || 0,
        title: title || null,
        subtitle: subtitle || null,
        ctaText: ctaText || null,
        ctaLink: ctaLink || null,
      },
    });
  };

  const handleDeleteConfirm = () => {
    if (deleteSlideId) {
      deleteMutation.mutate({ data: { slideId: deleteSlideId } });
    }
  };

  return (
    <div className="container mx-auto px-4 py-6 sm:px-6 sm:py-8 lg:px-8">
      {/* Breadcrumb */}
      <div className="mb-6">
        <Button asChild variant="ghost" className="mb-4">
          <Link to="/admin">
            <ArrowLeft className="mr-2 h-4 w-4" />
            Admin Paneli
          </Link>
        </Button>
      </div>

      {/* Header */}
      <div className="mb-6 flex flex-col gap-4 sm:flex-row sm:items-center sm:justify-between">
        <div>
          <h1 className="mb-2 font-bold text-2xl sm:text-3xl">
            Hero Slide Yönetimi
          </h1>
          <p className="text-muted-foreground text-sm sm:text-base">
            Ana sayfa hero slider'ı için slaytları yönetin
          </p>
        </div>

        <Dialog open={isAddDialogOpen} onOpenChange={setIsAddDialogOpen}>
          <DialogTrigger asChild>
            <Button>
              <Plus className="mr-2 h-4 w-4" />
              Yeni Slide
            </Button>
          </DialogTrigger>
          <DialogContent className="max-w-2xl">
            <DialogHeader>
              <DialogTitle>Yeni Hero Slide Ekle</DialogTitle>
              <DialogDescription>
                Hero slider için yeni bir görsel ekleyin
              </DialogDescription>
            </DialogHeader>

            <div className="space-y-4">
              {/* Image Selector */}
              <ImageSelector
                value={objectKey}
                onChange={(url) => {
                  // Extract key from URL
                  const key = url?.split("/").pop() || "";
                  setObjectKey(key);
                }}
                label="Görsel *"
                description="Hero slider'da gösterilecek görsel"
              />

              {/* Display Order */}
              <div className="space-y-2">
                <Label htmlFor="displayOrder">Görüntülenme Sırası</Label>
                <Input
                  id="displayOrder"
                  type="number"
                  min="0"
                  value={displayOrder}
                  onChange={(e) => setDisplayOrder(e.target.value)}
                  placeholder="0"
                />
                <p className="text-muted-foreground text-xs">
                  Küçük sayılar önce gösterilir
                </p>
              </div>

              {/* Title */}
              <div className="space-y-2">
                <Label htmlFor="title">Başlık (Opsiyonel)</Label>
                <Input
                  id="title"
                  value={title}
                  onChange={(e) => setTitle(e.target.value)}
                  placeholder="Slide başlığı"
                  maxLength={100}
                />
              </div>

              {/* Subtitle */}
              <div className="space-y-2">
                <Label htmlFor="subtitle">Alt Başlık (Opsiyonel)</Label>
                <Textarea
                  id="subtitle"
                  value={subtitle}
                  onChange={(e) => setSubtitle(e.target.value)}
                  placeholder="Slide alt başlığı"
                  maxLength={200}
                  rows={2}
                />
              </div>

              {/* CTA Text */}
              <div className="space-y-2">
                <Label htmlFor="ctaText">CTA Metin (Opsiyonel)</Label>
                <Input
                  id="ctaText"
                  value={ctaText}
                  onChange={(e) => setCtaText(e.target.value)}
                  placeholder="Buton metni"
                  maxLength={50}
                />
              </div>

              {/* CTA Link */}
              <div className="space-y-2">
                <Label htmlFor="ctaLink">CTA Link (Opsiyonel)</Label>
                <Input
                  id="ctaLink"
                  type="url"
                  value={ctaLink}
                  onChange={(e) => setCtaLink(e.target.value)}
                  placeholder="https://example.com"
                />
              </div>
            </div>

            <DialogFooter>
              <Button
                variant="outline"
                onClick={() => setIsAddDialogOpen(false)}
                disabled={createMutation.isPending}
              >
                İptal
              </Button>
              <Button
                onClick={handleCreateSlide}
                disabled={createMutation.isPending}
              >
                {createMutation.isPending && (
                  <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                )}
                Ekle
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      </div>

      {/* Slides Grid */}
      {slides.length === 0 ? (
        <Card>
          <CardContent className="flex flex-col items-center justify-center py-12 text-center">
            <h3 className="mb-2 font-semibold text-lg">Henüz slide yok</h3>
            <p className="mb-4 text-muted-foreground text-sm">
              Hero slider için görseller ekleyin
            </p>
            <Button onClick={() => setIsAddDialogOpen(true)}>
              <Plus className="mr-2 h-4 w-4" />
              İlk Slide'ı Ekle
            </Button>
          </CardContent>
        </Card>
      ) : (
        <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4">
          {slides.map((slide) => (
            <Card key={slide.slideId} className="overflow-hidden">
              <CardHeader className="p-0">
                <div className="relative aspect-video bg-muted">
                  {slide.url && (
                    <img
                      src={slide.url}
                      alt={slide.title || slide.key || "Hero slide"}
                      className="h-full w-full object-cover"
                    />
                  )}
                  {!slide.isActive && (
                    <div className="absolute inset-0 flex items-center justify-center bg-black/50">
                      <span className="rounded-md bg-background px-2 py-1 font-medium text-xs">
                        Pasif
                      </span>
                    </div>
                  )}
                </div>
              </CardHeader>
              <CardContent className="space-y-3 p-4">
                <div>
                  <p className="font-medium text-sm">
                    {slide.title || "Başlıksız"}
                  </p>
                  <p className="text-muted-foreground text-xs">
                    Sıra: {slide.displayOrder}
                  </p>
                </div>

                <div className="flex gap-2">
                  <Button
                    size="sm"
                    variant="outline"
                    className="flex-1"
                    onClick={() =>
                      toggleMutation.mutate({
                        data: { slideId: slide.slideId },
                      })
                    }
                    disabled={toggleMutation.isPending}
                  >
                    {slide.isActive ? (
                      <>
                        <Eye className="mr-1 h-4 w-4" />
                        Aktif
                      </>
                    ) : (
                      <>
                        <EyeOff className="mr-1 h-4 w-4" />
                        Pasif
                      </>
                    )}
                  </Button>
                  <Button
                    size="sm"
                    variant="destructive"
                    onClick={() => setDeleteSlideId(slide.slideId)}
                    disabled={deleteMutation.isPending}
                  >
                    <Trash2 className="h-4 w-4" />
                  </Button>
                </div>
              </CardContent>
            </Card>
          ))}
        </div>
      )}

      {/* Delete Confirmation Dialog */}
      <AlertDialog
        open={!!deleteSlideId}
        onOpenChange={() => setDeleteSlideId(null)}
      >
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>
              Slide'ı silmek istediğinize emin misiniz?
            </AlertDialogTitle>
            <AlertDialogDescription>
              Bu işlem geri alınamaz. Slide kalıcı olarak silinecektir.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel disabled={deleteMutation.isPending}>
              İptal
            </AlertDialogCancel>
            <AlertDialogAction
              onClick={handleDeleteConfirm}
              disabled={deleteMutation.isPending}
              className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
            >
              {deleteMutation.isPending && (
                <Loader2 className="mr-2 h-4 w-4 animate-spin" />
              )}
              Sil
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
